import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ScanSearch, Warehouse } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorTabs } from '../../components/OperatorTabs';
import { HomeStations } from './HomeStations';
import { HomeTasks } from './HomeTasks';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { operatorHomeTabChosen, operatorWarehouseSelected } from '../../../../features/operator/operatorSlice';
import {
  HOME_TAB,
  HOME_TAB_LABELS,
  OPERATOR_FLOW,
  OPERATOR_FLOW_LABELS,
  OPERATOR_FLOW_PATHS,
  OPERATOR_SCREEN,
  WAREHOUSES_SOURCE,
} from '../../../../features/operator/operatorConstants';
import {
  inactiveCompanies,
  isLookupOnly,
  selectCurrentOperatorWarehouse,
  selectHasTaskFlows,
  selectHomeTab,
  selectHomeTabs,
  selectShownFlows,
  shownFlows,
} from '../../../../features/operator/operatorUtils';
import { fetchTasks } from '../../../../features/operatorTasks/operatorTasksThunks';
import {
  selectTasksClock,
  selectTasksLoadedHere,
  selectTray,
  selectTrayTotal,
} from '../../../../features/operatorTasks/operatorTasksSlice';
import { startLabel, taskPath, updatedAgoText } from '../../../../features/operatorTasks/operatorTasksUtils';
import { warehouseKindLabel } from '../../../../utils/inventoryLabels';

const WarehousePicker = ({ warehouses, onPick }) => (
  <section className="space-y-3">
    <h1 className="text-xl font-bold text-ninja-dark dark:text-white">¿Dónde trabajas hoy?</h1>
    <ul className="space-y-3">
      {warehouses.map((warehouse) => {
        const flows = shownFlows(warehouse);
        return (
          <li key={warehouse.id}>
            <Button
              variant="choice"
              data-testid="warehouse-option"
              data-warehouse-id={warehouse.id}
              onClick={() => onPick(warehouse.id)}
            >
              <span className="flex items-center gap-3">
                <Warehouse size={24} className="shrink-0 text-ninja-green" />
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-bold text-ninja-dark dark:text-white">{warehouse.name}</span>
                  <span className="block text-sm text-text-secondary">
                    {[
                      warehouseKindLabel(warehouse.kind),
                      flows.length > 0 ? flows.map((flow) => OPERATOR_FLOW_LABELS[flow]).join(', ') : 'Sin flujos activos',
                    ].join(' · ')}
                  </span>
                </span>
                {warehouse.from_admin_fallback && (
                  <span className="shrink-0 rounded-full border border-border px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
                    De tu empleador
                  </span>
                )}
              </span>
            </Button>
          </li>
        );
      })}
    </ul>
  </section>
);

/**
 * Inicio: where the operator works today and, in two tabs, what to do next
 * and why (Tareas) or a station to work without a task (Trabajo libre). The
 * phone remembers the tab. The tray is asked every time Inicio opens, on
 * either tab, and once a minute while it sits still (`operatorRefresh`); who
 * the operator is, once per session.
 */
export const OperatorHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, source, warehouses } = useSelector((state) => state.operator);
  const warehouse = useSelector(selectCurrentOperatorWarehouse);
  // What it shows: a warehouse whose only flow is hidden says it has none.
  const flows = useSelector(selectShownFlows);
  const hasTasks = useSelector(selectHasTaskFlows);
  const tabs = useSelector(selectHomeTabs);
  const tab = useSelector(selectHomeTab);
  const trayTotal = useSelector(selectTrayTotal);
  const tray = useSelector(selectTray);
  const loadedHere = useSelector(selectTasksLoadedHere);
  const receivedAt = useSelector((state) => state.operatorTasks.receivedAt);
  const clock = useSelector(selectTasksClock);
  const warehouseId = warehouse?.id ?? null;

  useEffect(() => {
    dispatch(taskEntered({ flow: OPERATOR_SCREEN.HOME }));
  }, [dispatch]);

  // The tray moves all day: Inicio asks for it every time it opens. Changing tabs asks nothing.
  useEffect(() => {
    if (warehouseId !== null && hasTasks) dispatch(fetchTasks());
  }, [dispatch, warehouseId, hasTasks]);

  if (warehouses.length === 0) {
    return (
      <div data-testid="operator-no-warehouses" className="py-10 text-center space-y-2">
        <Warehouse size={32} className="mx-auto text-text-muted" />
        <p className="text-lg font-bold text-ninja-dark dark:text-white">No tienes bodegas para trabajar</p>
        <p className="text-sm text-text-secondary">Pídele a tu supervisor que te asigne una.</p>
      </div>
    );
  }

  if (!warehouse) {
    return <WarehousePicker warehouses={warehouses} onPick={(id) => dispatch(operatorWarehouseSelected(id))} />;
  }

  const blocked = inactiveCompanies(warehouse);
  // Trabajo libre has no bar: Consulta is one of its stations. Without tabs, Consulta keeps its button.
  const showBar = tab !== HOME_TAB.FREE;
  const next = tab === HOME_TAB.TASKS ? tray?.next ?? null : null;
  const canLookup = flows.includes(OPERATOR_FLOW.STOCK);
  const updated = loadedHere ? updatedAgoText(receivedAt, clock) : null;
  const tabOptions = tabs.map((value) => ({
    value,
    label: HOME_TAB_LABELS[value],
    count: value === HOME_TAB.TASKS ? trayTotal : null,
  }));

  let content;
  if (flows.length === 0) {
    content = (
      <div data-testid="operator-no-flows" className="space-y-2 rounded-2xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
        <p className="font-bold text-ninja-dark dark:text-white">Esta bodega no tiene flujos activos para ti</p>
        {blocked.map((company) => (
          <p key={company.name} className="text-sm text-text-secondary">
            {company.name}: {company.label}
          </p>
        ))}
      </div>
    );
  } else if (tab === null) {
    content = <HomeStations />;
  } else {
    content = (
      <>
        <OperatorTabs
          tabs={tabOptions}
          active={tab}
          onChange={(value) => dispatch(operatorHomeTabChosen(value))}
          testId="home-tab"
          groupTestId="home-tabs"
          dataKey="tab"
          label="Qué hacer"
        />
        <section data-testid="home-panel" data-tab={tab} aria-label={HOME_TAB_LABELS[tab]}>
          {tab === HOME_TAB.TASKS ? <HomeTasks /> : <HomeStations />}
        </section>
      </>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p data-testid="operator-greeting" className="text-xl font-bold text-ninja-dark dark:text-white">
            Hola{profile?.first_name ? `, ${profile.first_name}` : ''}
          </p>
          <p className="truncate text-sm text-text-secondary">{warehouse.name}</p>
        </div>
        {warehouses.length > 1 && (
          <Button
            variant="outline"
            size="lg"
            onClick={() => dispatch(operatorWarehouseSelected(null))}
            data-testid="operator-change-warehouse"
          >
            Cambiar
          </Button>
        )}
      </div>

      {source === WAREHOUSES_SOURCE.ADMIN_FALLBACK && (
        <p data-testid="operator-fallback-note" className="rounded-2xl border border-border bg-white p-3 text-sm text-text-secondary dark:bg-[#1a1a1a]">
          No tienes bodegas asignadas: trabajas en las de {profile?.employer?.name || 'tu empleador'}.
        </p>
      )}

      {isLookupOnly(warehouse) && (
        <p data-testid="operator-lite-note" className="rounded-2xl border border-border bg-white p-3 text-sm text-text-secondary dark:bg-[#1a1a1a]">
          Esta bodega tiene el módulo Lite: solo consulta.
        </p>
      )}

      {content}

      {hasTasks && (
        <div className="flex items-center gap-3">
          {updated && (
            <p data-testid="tasks-updated-at" className="text-sm text-text-secondary">
              {updated}
            </p>
          )}
          <PendingButton
            pendingOn={fetchTasks}
            variant="ghost"
            size="lg"
            icon={RefreshCw}
            loadingLabel="Actualizando…"
            onClick={() => dispatch(fetchTasks())}
            className="ml-auto"
            data-testid="operator-refresh"
          >
            Actualizar
          </PendingButton>
        </div>
      )}

      {showBar && (next || canLookup) && (
        <OperatorActionBar>
          {canLookup && (
            <Button variant="secondary" size="lg" icon={ScanSearch} onClick={() => navigate(OPERATOR_FLOW_PATHS.stock)}>
              {next ? 'Consultar' : 'Consultar un código'}
            </Button>
          )}
          {next && (
            <Button variant="primary" size="lg" onClick={() => navigate(taskPath(next))} data-testid="next-task-start">
              {`${startLabel(next)} ${next.reference}`}
            </Button>
          )}
        </OperatorActionBar>
      )}
    </div>
  );
};
